import styled from '@emotion/styled';
import { Box, Typography } from '@mui/material';
import Dialog, { DialogProps } from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import Paper, { PaperProps } from '@mui/material/Paper';
import { alpha } from '@mui/material/styles';
import { ReactNode } from 'react';
import Draggable from 'react-draggable';

interface DialogFlexSalesProps extends DialogProps {
  dialogTitle?: ReactNode;
  subtitle?: ReactNode;
  icon?: ReactNode;
  headerActions?: ReactNode;
}

const StyledPaper = styled(Paper)(({ theme }) => ({
  overflow: 'hidden',
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 24,
  background: theme.customTokens.surfaces.dialogGradient,
  backgroundColor: theme.palette.background.paper,
  boxShadow:
    theme.palette.mode === 'dark'
      ? `0 34px 100px ${alpha(theme.palette.common.black, 0.74)}`
      : `0 34px 100px ${alpha(theme.palette.text.primary, 0.32)}`,
  '& .MuiDialogTitle-root': {
    position: 'relative',
    cursor: 'move',
    padding: theme.spacing(2.5, 3),
    color: theme.palette.primary.contrastText,
    background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
    '&::after': {
      content: '""',
      position: 'absolute',
      inset: 0,
      pointerEvents: 'none',
      background: `radial-gradient(circle at 94% 0%, ${theme.customTokens.overlays.dialogHeaderHighlight}, transparent 18rem)`,
    },
  },
  '& .MuiDialogContent-root': {
    padding: theme.spacing(3),
    backgroundColor: theme.palette.background.paper,
  },
  '& .MuiDialogActions-root': {
    gap: theme.spacing(1),
    padding: theme.spacing(2, 3),
    borderTop: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.customTokens.surfaces.dialogActionsBackground,
  },
  [theme.breakpoints.down('sm')]: {
    width: 'calc(100% - 24px)',
    maxHeight: 'calc(100% - 24px)',
    margin: 12,
    borderRadius: 20,
    '& .MuiDialogTitle-root, & .MuiDialogContent-root, & .MuiDialogActions-root': {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
    },
  },
}));

const TitleContent = styled(Box)`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(2)};
`;

const TitleText = styled(Box)`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing(1.5)};
  min-width: 0;
`;

const HeaderIcon = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  flex: 0 0 auto;
  border-radius: 14px;
  background: ${({ theme }) => alpha(theme.palette.common.white, 0.16)};
`;

const PaperComponent = (props: PaperProps) => (
  <Draggable handle=".MuiDialogTitle-root" cancel={'[class*="MuiDialogContent-root"]'}>
    <StyledPaper {...props} />
  </Draggable>
);

const DialogFlexSales = (props: DialogFlexSalesProps) => {
  const {
    children,
    dialogTitle,
    subtitle,
    icon,
    headerActions,
    onClose,
    open,
    maxWidth,
    PaperProps,
    scroll,
    fullScreen,
    'aria-describedby': ariaDescribedBy,
    ...rest
  } = props;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperComponent={PaperComponent}
      aria-labelledby="draggable-dialog-title"
      maxWidth={maxWidth}
      PaperProps={PaperProps}
      scroll={scroll}
      fullScreen={fullScreen}
      aria-describedby={ariaDescribedBy}
      BackdropProps={{
        sx: {
          backgroundColor: theme => theme.customTokens.overlays.dialogBackdrop,
          backdropFilter: 'blur(2px)',
        },
      }}
      {...rest}
    >
      {dialogTitle && (
        <DialogTitle id="draggable-dialog-title">
          <TitleContent>
            <TitleText>
              {icon && <HeaderIcon>{icon}</HeaderIcon>}
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="h6">{dialogTitle}</Typography>
                {subtitle && (
                  <Typography
                    variant="body2"
                    sx={{
                      color: theme => alpha(theme.palette.primary.contrastText, 0.78),
                      mt: 0.25,
                    }}
                  >
                    {subtitle}
                  </Typography>
                )}
              </Box>
            </TitleText>
            {headerActions}
          </TitleContent>
        </DialogTitle>
      )}
      {children}
    </Dialog>
  );
};

export default DialogFlexSales;
